import React, { useState, useEffect } from "react";
import { GetCategoriasProductos } from "../../../services/categoriasServices";
import { GetProveedores } from "../../../services/proveedoresServices";
import {
  CalcularPrecioVenta,
  GetUnidadesMedida,
  PutProducto,
  PostProducto,
  UploadImagenProducto,
} from "../../../services/productosServices";

import AutoComplete from "../../utils/autoComplete";
import NavBar from "../../utils/nav/NavBar";
import Select from "../../utils/select";
import Checkbox from "../../utils/checkBox";

const EditProduct = ({ producto, onClose, onSave }) => {
  const [edit, setEdit] = useState({ ...producto });
  // UI state decoupled from value to avoid auto-uncheck when clearing the input
  const [usePesoPorPieza, setUsePesoPorPieza] = useState(
    Number(producto?.pesoPorPieza) > 0
  );
  const [pesoPorPiezaInput, setPesoPorPiezaInput] = useState(
    Number(producto?.pesoPorPieza) > 0 ? String(producto.pesoPorPieza) : ""
  );
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [imageToUpload, setImageToUpload] = useState();

  useEffect(() => {
    const cargarCatalogos = async () => {
      let categorias = await GetCategoriasProductos();
      let proveedores = await GetProveedores();
      let unidades = await GetUnidadesMedida();

      // En altas agrega item para seleccionar
      if (!producto.id) {
        categorias.splice(0, 0, { id: 0, nombre: "" });
        proveedores.splice(0, 0, { id: 0, nombre: "" });
        unidades.splice(0, 0, "");
      }

      setCategorias(categorias);
      setProveedores(proveedores);
      setUnidadesMedida(unidades);

      // On initialization, ensure edit.categoria is set if it's missing but we have the ID
      if (producto.categoriaProductoId && !edit.categoria) {
        let currentCat = categorias.find((x) => x.id === producto.categoriaProductoId);
        if (currentCat) {
          edit.categoria = currentCat;
          setEdit({ ...edit });
        }
      }
    };
    window.scrollTo({ top: 0 });
    cargarCatalogos();
  }, []);

  const handleChangeCategoria = (id) => {
    edit.categoriaProductoId = Number(id);
    let index = categorias.findIndex((x) => x.id === edit.categoriaProductoId);
    edit.categoria = categorias[index];

    if (edit.categoria && edit.categoria.subCategorias && edit.categoria.subCategorias.length > 0) {
      // If it has subcategories and current subcategory is not in this category, reset it
      if (!edit.categoria.subCategorias.find(x => x.id === edit.subCategoriaProductoId)) {
        edit.subCategoriaProductoId = null;
      }
    } else {
      edit.subCategoriaProductoId = null;
    }

    setEdit({ ...edit });
  };

  const handleChangeSubCategoria = (id) => {
    edit.subCategoriaProductoId = Number(id) === 0 ? null : Number(id);
    setEdit({ ...edit });
  };

  const handleChangeProveedor = (proveedor) => {
    if (proveedor) {
      edit.proveedorId = proveedor.id;
      edit.proveedor = proveedor;
    } else {
      edit.proveedorId = null;
      edit.proveedor = null;
    }
    setEdit({ ...edit });
  };

  const handleUploadImage = (e) => {
    let file = e.target.files[0];
    setImageToUpload(file);

    // Shows the image
    var reader = new FileReader();
    var imgtag = document.getElementById("productImage");
    imgtag.title = file.name;

    reader.onload = function (event) {
      imgtag.src = event.target.result;
    };

    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (!edit.descripcion) {
      alert("Escriba la descripción del producto");
      return false;
    }
    if (!edit.categoriaProductoId || edit.categoriaProductoId === 0) {
      alert("Seleccione la categoría del producto");
      return false;
    }
    if (
      edit.categoria &&
      edit.categoria.subCategorias &&
      edit.categoria.subCategorias.length > 0 &&
      (!edit.subCategoriaProductoId || edit.subCategoriaProductoId === 0)
    ) {
      alert("Seleccione la subcategoría del producto");
      return false;
    }
    if (!edit.proveedorId) {
      alert("Seleccione al proveedor del producto");
      return false;
    }
    if (!edit.unidad) {
      alert("Escriba la unidad de medida del producto");
      return false;
    }
    if (edit.intervalo <= 0) {
      alert("Escriba el intervalo del producto");
      return false;
    }
    if (edit.costoUnitario <= 0) {
      alert("Escriba el costo del producto");
      return false;
    }
    if (edit.margen <= 0) {
      alert("Indique el margen del producto");
      return false;
    }
    if (edit.montoSobrePrecio1 < 0) {
      alert("El monto de sobreprecio 1 es invalido");
      return;
    }
    if (edit.montoSobrePrecio2 < 0) {
      alert("El monto de sobreprecio 2 es invalido");
      return;
    }

    // Validación de Peso por pieza según checkbox
    if (usePesoPorPieza) {
      const parsed = Number(pesoPorPiezaInput);
      if (isNaN(parsed) || parsed <= 0) {
        alert("Capture el peso por pieza");
        return false;
      }
      edit.pesoPorPieza = parsed;
    } else {
      // Si no está marcado, forzar a 0
      edit.pesoPorPieza = 0;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      if (edit.id) await PutProducto(edit);
      else {
        let newProducto = { ...edit };
        newProducto.proveedor = null;
        newProducto.categoria = null;
        edit.id = await PostProducto(newProducto);
      }

      if (imageToUpload != null)
        edit.imagenUrl = await UploadImagenProducto(edit.id, imageToUpload);

      window.scrollTo({ top: 0 });
      onSave(edit);
    } catch (error) {
      alert(error);
    }
  };

  return (
    <div id="edit-product">
      <div className="page-header">
        <NavBar
          title={!edit.id ? "Nuevo producto" : "Editar producto"}
          navButtonComponent={
            <i
              className="fas fa-arrow-left nav-button-back"
              onClick={() => onClose()}
            />
          }
        />
      </div>
      <div className="page--content">
        <div className="card">
          <div className="form">
            <label htmlFor="descripcion">Descripción:</label>
            <input
              type="text"
              value={edit.descripcion}
              onChange={(e) => {
                edit.descripcion = e.currentTarget.value;
                setEdit({ ...edit });
              }}
            />

            <label htmlFor="categoria">Categoría:</label>
            <Select
              value={edit.categoriaProductoId}
              items={categorias}
              itemKey={(item) => item.id}
              itemDisplay={(item) => item.nombre}
              onChange={(e) => handleChangeCategoria(e.currentTarget.value)}
            />

            {edit.categoria &&
              edit.categoria.subCategorias &&
              edit.categoria.subCategorias.length > 0 && (
                <>
                  <label htmlFor="subCategoria">Subcategoría:</label>
                  <Select
                    value={edit.subCategoriaProductoId}
                    items={
                      edit.subCategoriaProductoId === null
                        ? [{ id: 0, nombre: "" }, ...edit.categoria.subCategorias]
                        : edit.categoria.subCategorias
                    }
                    itemKey={(item) => item.id}
                    itemDisplay={(item) => item.nombre}
                    onChange={(e) =>
                      handleChangeSubCategoria(e.currentTarget.value)
                    }
                  />
                </>
              )}
            
            

            <label>Proveedor:</label>
            <AutoComplete
              items={proveedores}
              value={producto.proveedor}
              itemKey={(item) => item.id}
              itemDisplay={(item) => item.nombre}
              onItemSelected={(e) => handleChangeProveedor(e)}
            />

            <label htmlFor="unidad">Unidad:</label>
            <Select
              items={unidadesMedida}
              itemKey={(item) => item}
              itemDisplay={(item) => item}
              value={edit.unidad}
              onChange={(e) => {
                edit.unidad = e.currentTarget.value;
                setEdit({ ...edit });
              }}
            />

            <div className="grid gap-small intervalo">
              <div>
                <label>Intervalo:</label>
                <input
                  type="number"
                  value={edit.intervalo}
                  onChange={(e) => {
                    edit.intervalo = e.currentTarget.value;
                    setEdit({ ...edit });
                  }}
                />
              </div>
            </div>
            <div style={{ paddingBottom: "1rem", display: "flex" }}>
              <Checkbox
                checked={usePesoPorPieza}
                label="Peso/pieza"
                onCheckedChanged={() => {
                  const next = !usePesoPorPieza;
                  setUsePesoPorPieza(next);
                  if (!next) {
                    // Al desactivar, limpiar el valor efectivo
                    edit.pesoPorPieza = 0;
                    setEdit({ ...edit });
                  } else {
                    // Mantener el valor capturado si existe; si no, sugerir 1 como inicio
                    if (pesoPorPiezaInput === "") {
                      setPesoPorPiezaInput("1");
                    }
                  }
                }}
              />
              {usePesoPorPieza && (
                <div style={{ marginLeft: "1rem" }}>
                  <input
                    type="number"
                    value={pesoPorPiezaInput}
                    onChange={(e) => {
                      setPesoPorPiezaInput(e.currentTarget.value);
                    }}
                  />
                </div>
              )}
            </div>
            <div className="div-between">
              <Checkbox
                checked={edit.activo}
                label="Activo"
                onCheckedChanged={() => {
                  edit.activo = !edit.activo;
                  setEdit({ ...edit });
                }}
              />

              <Checkbox
                checked={edit.agotado}
                label="Agotado"
                onCheckedChanged={() => {
                  edit.agotado = !edit.agotado;
                  setEdit({ ...edit });
                }}
              />
            </div>

            <div style={{display: "flex", justifyContent: "space-between", marginTop: "1rem", gap: "1rem" }}>
              <div>
                <label>MOQ:</label>
                <input
                    type="number"
                    value={edit.moq}
                    onChange={(e) => {
                      edit.moq = e.currentTarget.value;
                      setEdit({ ...edit });
                    }}
                />
              </div>
              <div>
                <label>Tiempo vida (días):</label>
                <input
                    type="number"
                    value={edit.tiempoVida}
                    onChange={(e) => {
                      edit.tiempoVida = e.currentTarget.value;
                      setEdit({ ...edit });
                    }}
                />
              </div>
            </div>
            
          </div>

          <hr />

          <div className="edit-product--image-container">
            <div>
              <img id="productImage" src={producto.imagenUrl} alt="" />
            </div>
            <input
              type="file"
              name="pickFile"
              id="pickFile"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleUploadImage}
            />
            <button
              className="btn primary"
              onClick={() => document.getElementById("pickFile").click()}
            >
              Seleccionar imagen
            </button>
          </div>
        </div>

        <div className="card edit-product--precios">
          <div className="form">
            <label>Costo:</label>
            <input
              type="number"
              value={edit.costoUnitario}
              onChange={(e) => {
                edit.costoUnitario = Number(e.currentTarget.value);
                edit.precio = CalcularPrecioVenta(edit);
                setEdit({ ...edit });
              }}
            />

            <label>Margen:</label>
            <input
              type="number"
              value={edit.margen}
              onChange={(e) => {
                edit.margen = Number(e.currentTarget.value);
                edit.precio = CalcularPrecioVenta(edit);
                setEdit({ ...edit });
              }}
            />

            <div className="intervalo">
              <input
                type="text"
                placeholder="Concepto"
                value={edit.conceptoSobrePrecio1}
                onChange={(e) => {
                  edit.conceptoSobrePrecio1 = e.currentTarget.value;
                  setEdit({ ...edit });
                }}
              />
              <input
                type="number"
                placeholder="Monto"
                value={edit.montoSobrePrecio1}
                onChange={(e) => {
                  edit.montoSobrePrecio1 = Number(e.currentTarget.value);
                  edit.precio = CalcularPrecioVenta(edit);
                  setEdit({ ...edit });
                }}
              />
              <input
                type="text"
                placeholder="Concepto"
                value={edit.conceptoSobrePrecio2}
                onChange={(e) => {
                  edit.conceptoSobrePrecio2 = e.currentTarget.value;
                  setEdit({ ...edit });
                }}
              />
              <input
                type="number"
                placeholder="Monto"
                value={edit.montoSobrePrecio2}
                onChange={(e) => {
                  edit.montoSobrePrecio2 = Number(e.currentTarget.value);
                  edit.precio = CalcularPrecioVenta(edit);
                  setEdit({ ...edit });
                }}
              />
            </div>

            <div className="products--precio">
              <label>Precio:</label>
              <label>$ {edit.precio}</label>
            </div>
          </div>
        </div>

        <div className="page-footer">
          <button className="btn secondary" onClick={handleSave}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
